import React from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, CircularProgress } from '@mui/material';
import { DomainRegistryInfo } from '../../types/domainRegistry';

/** Props every domain management tab receives. */
export interface DomainTabProps {
  serviceId: string;
  domain: string;
  info: DomainRegistryInfo | null;
  /** Admins can unlock transfers, change the auth code and edit DNSSEC. */
  isAdmin: boolean;
  /** Reload the cached registry info after a change. */
  onChanged: () => Promise<void> | void;
}

export const DNS_CONFIG_LABELS: Record<number, string> = {
  1: 'Custom name servers',
  2: 'Email / web forwarding',
  3: 'Parked',
  4: 'Synergy DNS hosting',
};

export const dnsConfigOf = (info: DomainRegistryInfo | null): number | null => {
  const n = parseInt(info?.dns_config || '', 10);
  return Number.isFinite(n) ? n : null;
};

export const errorText = (e: unknown, fallback = 'Something went wrong.'): string => {
  const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

/** "Something changed at Synergy" notice with a Reload button, used when a tab can't be used in the current DNS mode. */
export const ModeNotice: React.FC<{ current: number | null; needs: number; what: string; onGoToNameservers?: () => void }> = ({ current, needs, what, onGoToNameservers }) => {
  if (current === null || current === needs) return null;
  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
      action={onGoToNameservers ? <Button color="inherit" size="small" onClick={onGoToNameservers}>Name servers</Button> : undefined}
    >
      {what} only work when the DNS mode is “{DNS_CONFIG_LABELS[needs]}”. This domain is set to “{DNS_CONFIG_LABELS[current] || 'something else'}”.
    </Alert>
  );
};

export const Confirm: React.FC<{
  open: boolean;
  title: string;
  text: React.ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, title, text, confirmLabel = 'Confirm', danger, busy, onCancel, onConfirm }) => (
  <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <DialogContentText component="div">{text}</DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} disabled={busy}>Cancel</Button>
      <Button onClick={onConfirm} variant="contained" color={danger ? 'error' : 'primary'} disabled={busy}>
        {busy ? <CircularProgress size={20} /> : confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

export const TabBody: React.FC<{ children: React.ReactNode }> = ({ children }) => <Box sx={{ pt: 1 }}>{children}</Box>;
